import { z } from "zod";

// The Foyr ontology, expressed as zod schemas. Every node/edge passing a
// system boundary (network, localStorage, URL params, demo JSON) is parsed
// through one of these. CLAUDE.md §2 — strict types end-to-end.

export const NodeKindSchema = z.enum([
  "Project",
  "Client",
  "Intent",
  "Style",
  "Room",
  "Zone",
  "Placement",
  "SKU",
  "Material",
  "Vendor",
  "BudgetLine",
  "Milestone",
  "Approval",
  "Rule",
  "Alert",
  "MemoryCard",
  "FloorPlan",
  // PROV-O typed activities + entities — emitted by ingest pipelines so
  // every artefact carries an audit trail back to its origin.
  "CatalogImport",
  "Inspiration",
  "TimelineEvent",
  // Drawing surfaces declared in codegen.json (ReflectedCeilingPlan etc.
  // exist in the generated enum but aren't yet authored at runtime — add
  // them here when the renderer ships per /drawings hub).
]);
export type NodeKind = z.infer<typeof NodeKindSchema>;

export const EdgeKindSchema = z.enum([
  "HAS_ROOM",
  "HAS_INTENT",
  "HAS_MILESTONE",
  "OWNS",
  "APPROVED",
  "REJECTED",
  "CONTAINS",
  "ADJACENT_TO",
  "WITHIN",
  "OF_SKU",
  "IN_ROOM",
  "MADE_OF",
  "MATCHES_STYLE",
  "SUBSTITUTES",
  "SUPPLIED_BY",
  "OFFERS",
  "DELIVERS_BY",
  "FOR_ROOM",
  "FOR_SKU",
  "APPROVED_BY",
  "BLOCKED_BY",
  "DEPENDS_ON",
  "OF",
  "RULE_FIRED_FROM",
  "RULE_RECOMMENDS",
  "RULE_BLOCKED_BY",
  "PROMOTED_FROM",
  "APPLIES_TO",
  "HAS_FLOORPLAN",
  // PROV-O provenance edges — every typed Activity has SKUs / Suggestions
  // / Approvals pointing at it so the Why-engine can walk the chain.
  "WAS_GENERATED_BY",
  "WAS_INFORMED_BY",
  "WAS_ASSOCIATED_WITH",
  "HAS_INSPIRATION",
  "DERIVED_FROM_INSPIRATION",
]);
export type EdgeKind = z.infer<typeof EdgeKindSchema>;

export const Money = z.object({
  amount: z.number(),
  currency: z.enum(["INR", "USD", "EUR", "GBP", "AED", "SGD"]),
});
export type Money = z.infer<typeof Money>;

export const GraphNodeSchema = z.object({
  id: z.string(),
  kind: NodeKindSchema,
  label: z.string(),
  attrs: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
  position: z.tuple([z.number(), z.number()]).optional(),
  styleVec: z.array(z.number()).optional(),
});
export type GraphNode = z.infer<typeof GraphNodeSchema>;

export const GraphEdgeSchema = z.object({
  id: z.string(),
  kind: EdgeKindSchema,
  source: z.string(),
  target: z.string(),
  weight: z.number().min(0).max(1).default(0.5),
  attrs: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
});
export type GraphEdge = z.infer<typeof GraphEdgeSchema>;

export const ProjectGraphSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  nodes: z.array(GraphNodeSchema),
  edges: z.array(GraphEdgeSchema),
});
export type ProjectGraph = z.infer<typeof ProjectGraphSchema>;

// Ontology palette: every NodeKind has a colour, a glyph hint, and a
// human-readable description. Never hardcode colours per component;
// always read from here. CLAUDE.md §9.
export const NODE_PALETTE: Record<NodeKind, { hex: string; soft: string; label: string }> = {
  Project:    { hex: "#0a0a0a", soft: "#cccccc", label: "Project" },
  Client:     { hex: "#3D6B9D", soft: "#A8C2DD", label: "Client" },
  Intent:     { hex: "#7A3E5F", soft: "#D6B7C5", label: "Intent" },
  Style:      { hex: "#6B5B4D", soft: "#C9BBA9", label: "Style" },
  Room:       { hex: "#1a1a1a", soft: "#cccccc", label: "Room" },
  Zone:       { hex: "#4a4a4a", soft: "#D4CFC6", label: "Zone" },
  Placement:  { hex: "#3F7A55", soft: "#B3D4BF", label: "Placement" },
  SKU:        { hex: "#C95C1A", soft: "#E8B591", label: "SKU" },
  Material:   { hex: "#8B7968", soft: "#D9C9B8", label: "Material" },
  Vendor:     { hex: "#3D6B9D", soft: "#A8C2DD", label: "Vendor" },
  BudgetLine: { hex: "#3F7A55", soft: "#B3D4BF", label: "Budget" },
  Milestone:  { hex: "#A04612", soft: "#E8B591", label: "Milestone" },
  Approval:   { hex: "#7A3E5F", soft: "#D6B7C5", label: "Approval" },
  Rule:       { hex: "#0a0a0a", soft: "#F0EDE8", label: "Rule" },
  Alert:      { hex: "#C95C1A", soft: "#FCE6D2", label: "Alert" },
  MemoryCard: { hex: "#6B5B4D", soft: "#E6E2DC", label: "Memory" },
  FloorPlan:  { hex: "#3D6B9D", soft: "#C3D4E4", label: "Floor plan" },
  CatalogImport: { hex: "#3F7A55", soft: "#B3D4BF", label: "Catalog import" },
  Inspiration:   { hex: "#7A3E5F", soft: "#D6B7C5", label: "Inspiration" },
  TimelineEvent: { hex: "#141612", soft: "#E6E2DC", label: "Timeline event" },
};

// Predicate fields exposed to the Rule Builder. The autocomplete in the
// IF node reads this list — keeping the canonical list here lets the
// editor stay in sync with what the solver can actually evaluate.
export interface PredicateField {
  path: string;       // e.g. "SKU.leadDays"
  kind: NodeKind;
  field: string;
  type: "number" | "string" | "date" | "vector" | "boolean";
  unit?: string;
}

export const PREDICATE_FIELDS: PredicateField[] = [
  { path: "SKU.leadDays",         kind: "SKU",         field: "leadDays",  type: "number", unit: "days" },
  { path: "SKU.price",            kind: "SKU",         field: "price",     type: "number", unit: "₹" },
  { path: "SKU.styleMatch",       kind: "SKU",         field: "styleMatch",type: "number", unit: "%" },
  { path: "Milestone.dueIn",      kind: "Milestone",   field: "dueIn",     type: "number", unit: "days" },
  { path: "Milestone.dueOn",      kind: "Milestone",   field: "dueOn",     type: "date",   unit: "xsd:dateTime" },
  { path: "Milestone.slackDays",  kind: "Milestone",   field: "slackDays", type: "number", unit: "days" },
  { path: "BudgetLine.amount",    kind: "BudgetLine",  field: "amount",    type: "number", unit: "₹" },
  { path: "BudgetLine.share",     kind: "BudgetLine",  field: "share",     type: "number", unit: "%" },
  { path: "Project.budgetCap",    kind: "Project",     field: "budgetCap", type: "number", unit: "₹" },
  { path: "Vendor.rating",        kind: "Vendor",      field: "rating",    type: "number", unit: "/5" },
  { path: "Vendor.onTimeRate",    kind: "Vendor",      field: "onTimeRate",type: "number", unit: "%" },
  { path: "Room.area",            kind: "Room",        field: "area",      type: "number", unit: "sq ft" },
  { path: "Approval.ageDays",     kind: "Approval",    field: "ageDays",   type: "number", unit: "days" },
];
